import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSearch, FiX, FiChevronUp, FiChevronDown } = FiIcons

const SearchSection = () => {
  const {
    currentLang,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    handleSort,
    translations,
    clearSearch,
    isSearching
  } = useApp()

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  const handleClearSearch = () => {
    clearSearch()
  }

  const sortOptions = [
    { field: 'code', label: translations.sortByCode[currentLang] },
    { field: 'nameEn', label: translations.sortByName[currentLang] }
  ]

  const getSortStatusText = () => {
    const direction = sortDirection === 'asc' ? 'asc' : 'desc'
    const field = sortField === 'code' ? 'code' : 'name'
    return translations.sortStatus?.[currentLang]?.[field]?.[direction] || ''
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="mb-8"
    >
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
        {/* Mobile Layout - Stack vertically */}
        <div className="block sm:hidden space-y-4">
          {/* Search Input */}
          <div className="w-full">
            <label 
              htmlFor="search-input"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {translations.searchLabel[currentLang]}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon 
                  icon={FiSearch} 
                  className="h-5 w-5 text-gray-400" 
                />
              </div>
              <input
                id="search-input"
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={translations.searchPlaceholder[currentLang]}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 dark:hover:bg-gray-600 rounded-r-xl transition-colors duration-200"
                  title={translations.clearSearch[currentLang]}
                >
                  <SafeIcon 
                    icon={FiX} 
                    className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" 
                  />
                </button>
              )}
            </div>
          </div>

          {/* Sort Controls */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {translations.sortBy[currentLang]}
            </label>
            <div className="flex flex-col gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.field}
                  onClick={() => handleSort(option.field)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition-all duration-200 text-sm font-medium ${
                    sortField === option.field
                      ? 'bg-purple-500 text-white border-purple-500 shadow-md'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <span>{option.label}</span>
                  {sortField === option.field && (
                    <SafeIcon 
                      icon={sortDirection === 'asc' ? FiChevronUp : FiChevronDown}
                      className="w-4 h-4"
                    />
                  )}
                </button>
              ))}
            </div>
            {/* Sort Status - 移到排序控制下方 */}
            <div className="mt-3 text-sm text-purple-600 dark:text-purple-400 flex items-center gap-2">
              <SafeIcon 
                icon={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                className="w-4 h-4" 
              />
              <span>{getSortStatusText()}</span>
            </div>
          </div>
        </div>

        {/* Desktop Layout - Side by side with proper alignment */}
        <div className="hidden sm:block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Search Input */}
            <div className="w-full">
              <label 
                htmlFor="search-input-desktop"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {translations.searchLabel[currentLang]}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon 
                    icon={FiSearch} 
                    className="h-5 w-5 text-gray-400" 
                  />
                </div>
                <input
                  id="search-input-desktop"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={translations.searchPlaceholder[currentLang]}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200 text-base"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 dark:hover:bg-gray-600 rounded-r-xl transition-colors duration-200"
                    title={translations.clearSearch[currentLang]}
                  >
                    <SafeIcon 
                      icon={FiX} 
                      className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" 
                    />
                  </button>
                )}
              </div>
            </div>

            {/* Sort Controls */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translations.sortBy[currentLang]}
              </label>
              <div className="flex gap-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.field}
                    onClick={() => handleSort(option.field)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition-all duration-200 text-base font-medium flex-1 ${
                      sortField === option.field
                        ? 'bg-purple-500 text-white border-purple-500 shadow-md'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <span>{option.label}</span>
                    {sortField === option.field && (
                      <SafeIcon 
                        icon={sortDirection === 'asc' ? FiChevronUp : FiChevronDown}
                        className="w-4 h-4"
                      />
                    )}
                  </button>
                ))}
              </div>
              {/* Sort Status - 移到排序控制下方 */}
              <div className="mt-3 text-sm text-purple-600 dark:text-purple-400 flex items-center gap-2">
                <SafeIcon 
                  icon={sortDirection === 'asc' ? FiChevronUp : FiChevronDown} 
                  className="w-4 h-4" 
                />
                <span>{getSortStatusText()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

export default SearchSection